/**
 * Adaptateur moteur (ADR 0003 §3, ADR 0004 §4). TOUT appel au moteur passe par ici ; aucune logique métier :
 * uniquement des conversions de signature entre `engine/src` et les écrans.
 *
 * Moteur réel (`engine/src/index.ts`) :
 *   simulate(baseline, params, programme, financing, choices, seed, { measures, events }) -> Timeline
 *   needGap(programme, catalogue) -> Md€/an en régime
 *   previewProgramme(baseline, params, programme, financing, catalogue, seed) -> ProgrammePreview (= FinancingPreview ici)
 *   measureTrajectory(baseline, params, catalogue, id, hyp?) -> TrajectoryPoint[] (clés d_*)
 *   simulateWithState + projectRemaining(game, state) -> YearState[] « si rien ne change »
 *   Timeline.verdict { reelected, score, score_max, blocked_by }
 * Clés `YearState.kpis` (vocabulaire partagé, ADR 0004) : deficit_pct_gdp, debt_pct_gdp, growth_pct, unemployment_pct,
 *   poverty_pct, confidence, cohesion, political_capital, eu_relation, co2_mt, trade_balance_bn, interest_bn,
 *   apparent_rate_pct, marginal_rate_pct, gdp_bn, debt_bn, inflation_pct, potential_growth_pct, impulse_pt,
 *   years_over_deficit_rule, trade_growth_contrib_pt.
 *
 * v0.2 (ADR 0005 moteur, ADR 0006 UI, ADR 0008 intégration) — exports réels du moteur :
 *   KPIs `parliament_majority` (0–100), `purchasing_power_index` (base 100), `purchasing_power_change`, `cpi_policy_pct`,
 *     `price_<poste>_pct`, `state_interest_bn`, `output_gap_pct`, `shock_growth_pt`, `er_wait_hours`, `rail_incidents_index`,
 *     `court_delay_months`, `pupils_per_class`, `prison_occupancy_pct` → `caps.kpis` (clés présentes dans une Timeline sonde).
 *   `priceEffects(params, programme, financing, catalogue)` → `{ n1, n5 }`, chacun `{ prices: {bread, fuel, rent, energy},
 *     cpi, income, by_source[] }` en Δ % → normalisé en `{ bread: { n1, n5 }, … }`.
 *   `attribute2032(baseline, params, programme, financing, choices, seed, content)` → `{ items: [{ kind, id, option_id?, label,
 *     delta: { deficit_pct_gdp, debt_pct_gdp, unemployment_pct, purchasing_power_index, growth_avg_pct }, rationale }] }` ;
 *     delta = partie réelle − partie sans l'élément. `deficit_pct_gdp` est le SOLDE signé (négatif = déficit) : delta > 0 =
 *     solde amélioré = déficit réduit (vérifié par ui/tests/adapter : une mesure de recette pure donne delta > 0).
 *   Variantes de mesures : `programme.variants[mesureId] = varianteId`, lu nativement par le moteur (`applyVariant`).
 *
 * Le mock (`ui/src/dev/mock-engine.ts`) n'est utilisé que si l'URL porte `?mock=1` (développement) ou si le moteur réel
 * refuse le contenu chargé (erreur affichée dans le bandeau).
 */
import * as engine from "../../engine/src/index.js";
import type { Baseline, Choices, Financing, Param, ParamSet, PresetProgramme, Programme, ScorecardContent, ScorecardDefs, SimOptions, Timeline, YearState } from "../../engine/src/types.js";
import type { FinancingLever, UiFinancing, UiGameEvent, UiMeasure, UiProgramme } from "./content-types.js";
import { mockEngine } from "./dev/mock-engine.js";

export interface EngineContext {
  baseline: Baseline;
  params: ParamSet;
  measures: UiMeasure[];
  events: UiGameEvent[];
  levers: FinancingLever[];
  /** Bilan chiffré (`content/scorecard.json`) et préréglages (`content/presets.json`), ADR 0015 ; absents = bilan indisponible. */
  scorecard?: ScorecardDefs | null;
  presets?: PresetProgramme[];
}

const simOpts = (mode?: SimMode): SimOptions | undefined => (mode?.realistic ? { realistic: true } : undefined);

/** Contenu du bilan pour le moteur ; null si les définitions ou le préréglage de référence manquent. */
function scorecardContent(ctx: EngineContext): ScorecardContent | null {
  const sc = ctx.scorecard;
  const presets = ctx.presets ?? [];
  if (!sc || !presets.some((p) => p.id === sc.reference_preset_id)) return null;
  return { measures: ctx.measures, events: ctx.events, scorecard: sc, presets };
}

/** Clés KPI v0.2 attendues du moteur (vocabulaire, pas des chiffres). */
export const V02_KPIS = [
  "parliament_majority",
  "purchasing_power_index",
  "purchasing_power_change",
  "cpi_policy_pct",
  "state_interest_bn",
  "output_gap_pct",
  "shock_growth_pt",
  "er_wait_hours",
  "rail_incidents_index",
  "court_delay_months",
  "pupils_per_class",
  "prison_occupancy_pct",
] as const;
export type V02Kpi = (typeof V02_KPIS)[number];

/** Effet sur les prix d'un poste, en Δ % : l'année suivante (n1) et à 5 ans (n5). */
export interface PriceDelta {
  n1: number;
  n5: number;
}
export type PriceItem = "bread" | "fuel" | "rent" | "energy";
export type PriceEffects = Partial<Record<PriceItem, PriceDelta>>;

/**
 * Attribution de l'écart 2031/2032 (H1). Conventions de signe = celles des KPIs : `d_deficit_pct` est l'écart du
 * SOLDE public en points de PIB (positif = déficit réduit), `d_debt_pct` en points de PIB (positif = dette plus lourde),
 * `d_unemployment_pt` (positif = plus de chômage), `d_purchasing_power` en points d'indice, `d_growth_pt` en points.
 */
export interface AttributionItem {
  /** v0.3 (supposé) : `shock` = choc subi par un événement, hors décision (libellé « Choc subi : … »). */
  kind: "measure" | "event" | "financing" | "shock";
  id: string;
  label: string;
  /** Événement : option retenue et option de référence de la comparaison (v0.3, `reference_option_id`). */
  option_id?: string;
  reference_option_id?: string;
  d_deficit_pct?: number;
  d_debt_pct?: number;
  d_unemployment_pt?: number;
  d_purchasing_power?: number;
  d_growth_pt?: number;
  rationale?: string;
}

export interface EngineCaps {
  /** KPIs v0.2 présents dans la Timeline. */
  kpis: Set<string>;
  priceEffects: boolean;
  attribution: boolean;
  /** Variantes lues nativement par le moteur (`programme.variants`) ; false = sélecteur désactivé (mock). */
  variants: boolean;
  /** v0.3 : le moteur lit `financing.mission_cuts` (la simulation change quand on en fournit). */
  missionCuts: boolean;
  /** v0.3 : `timeline.verdict.rules[]` fourni par le moteur. */
  verdictRules: boolean;
}

/**
 * Règle du verdict (v0.3, supposé : `timeline.verdict.rules[] { id, label, value, threshold, ok, unit? }`). En repli, l'UI
 * dérive les règles des paramètres du verdict actuel (`derived: true`) : seuils lus dans params, jamais en dur.
 */
export interface VerdictRule {
  id: string;
  label: string;
  value: number;
  threshold: number;
  ok: boolean;
  unit?: string;
  /** Sens de la condition pour la phrase (« limite » pour un plafond, « minimum » pour un plancher). */
  kind?: "max" | "min";
  /** Règle dérivée par l'UI (moteur sans `verdict.rules`). */
  derived?: boolean;
  /** Condition pour gouverner (censure), pas pour être réélu. */
  governing?: boolean;
  /** Paramètre de seuil (écran Hypothèses) ; `baseline.*` = ancrage institutionnel lu dans la trajectoire officielle. */
  param?: string;
  /** Voie alternative (déficit : réduction depuis le départ, ADR 0014 §4). */
  alt?: { label: string; value: number; threshold: number; ok: boolean };
}

export type TrajectoryPoint = engine.TrajectoryPoint;
export type { UiProgramme };
export type FinancingPreview = engine.ProgrammePreview;

export interface Verdict {
  score: number;
  score_max: number;
  reelected: boolean;
  headline: string;
  /** Valeurs brutes « début → fin » ; l'UI formate. */
  lines: Array<{ label: string; from: number; to: number; unit: "%" | "Md€" | "Mt" | ""; dec: number }>;
  /** Raisons d'une non-réélection, en français. */
  flags: string[];
  /** Règles du verdict, valeur contre seuil (moteur v0.3 ou dérivées des params). */
  rules: VerdictRule[];
}

/** Mode de simulation (ADR 0015) : `realistic` = sans mécaniques politiques non sourcées (pas de censure ni de crise de dette par l'indice de confiance). */
export interface SimMode {
  realistic: boolean;
}

export type Scorecard = engine.Scorecard;
/** Résumé pondéré par les priorités du joueur (moteur pur, ADR 0015) : jamais appliqué par défaut. */
export const weightedShares = engine.weightedSummary;
export type ScorecardExplanation = engine.ScorecardExplanation;

export interface EngineApi {
  name: string;
  isMock: boolean;
  /** Capacités v0.2 (renseignées par `getEngine` après sonde). */
  caps: EngineCaps;
  simulate(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number, mode?: SimMode): Timeline;
  /** Budget tendanciel : programme vide, sans événement (écran « Ce qui existe »). */
  trend(ctx: EngineContext, seed: number, mode?: SimMode): Timeline;
  /**
   * Bilan chiffré comparé (ADR 0015) : joueur / « si rien ne change » / budget du gouvernement, même graine, mêmes crises ;
   * null si le moteur ou le contenu (`scorecard.json`, préréglage de référence) ne le permettent pas.
   */
  scorecard(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number, mode: SimMode): Scorecard | null;
  /** « Pourquoi ? » par indicateur, contre le tendanciel et contre le gouvernement ; null si indisponible. */
  explainScorecard(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number, mode: SimMode): ScorecardExplanation | null;
  /** Effets prix (Δ %) d'un programme + financement ; null si le moteur ne les fournit pas. */
  priceEffects(ctx: EngineContext, programme: Programme, financing: Financing): PriceEffects | null;
  /** « Pourquoi ? » : contributions par mesure / événement / financement ; null si indisponible. */
  attribute2032(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number): AttributionItem[] | null;
  needGap(ctx: EngineContext, programme: Programme): number;
  previewFinancing(ctx: EngineContext, programme: Programme, financing: Financing, seed: number): FinancingPreview;
  measureTrajectory(ctx: EngineContext, measureId: string, hyp: Record<string, number>): TrajectoryPoint[];
  projectRemaining(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number, fromYearIndex: number, mode?: SimMode): YearState[];
  /** @deprecated ADR 0015 : « réélu / battu » n'est plus affiché ; conservé pour les règles et le repli du moteur de développement. */
  verdict(ctx: EngineContext, timeline: Timeline): Verdict;
}

/** Lignes « début → fin » du verdict : clé KPI, libellé, chiffre de baseline correspondant. Vocabulaire, pas des chiffres. */
const VERDICT_LINES: Array<{ key: string; label: string; unit: Verdict["lines"][number]["unit"]; dec: number; base: [keyof Baseline, string] }> = [
  { key: "debt_pct_gdp", label: "Dette / PIB", unit: "%", dec: 0, base: ["apu", "debt_pct_gdp"] },
  { key: "deficit_pct_gdp", label: "Déficit public / PIB", unit: "%", dec: 1, base: ["apu", "deficit_pct_gdp"] },
  { key: "unemployment_pct", label: "Chômage", unit: "%", dec: 1, base: ["macro", "unemployment_rate"] },
  { key: "poverty_pct", label: "Pauvreté", unit: "%", dec: 1, base: ["macro", "poverty_rate"] },
  { key: "co2_mt", label: "Émissions", unit: "Mt", dec: 0, base: ["macro", "ghg_emissions"] },
  { key: "trade_balance_bn", label: "Balance commerciale", unit: "Md€", dec: 0, base: ["trade", "balance_bn"] },
  { key: "eu_relation", label: "Relation UE", unit: "", dec: 0, base: ["politics", "eu_relation"] },
  { key: "confidence", label: "Confiance des marchés", unit: "", dec: 0, base: ["politics", "confidence"] },
];

/** Raisons de non-réélection (règle de déficit : ancrage institutionnel, ADR 0014 §4 ; rien en dur). */
function blockedLabel(params: ParamSet, b: engine.Verdict["blocked_by"][number]): string {
  void params;
  switch (b) {
    case "capital":
      return "Capital politique insuffisant pour être réélu.";
    case "cohesion":
      return "Cohésion sociale sous le seuil de réélection.";
    case "confidence":
      return "Confiance des marchés sous le seuil : les créanciers ont voté avant les électeurs.";
    case "deficit":
      return "Déficit final ni réduit depuis le départ, ni conforme à la trajectoire européenne du plan de moyen terme.";
    case "majority":
      return "Majorité au Parlement perdue en fin de mandat.";
    case "purchasing_power":
      return "Pouvoir d'achat en fin de mandat sous le seuil de réélection.";
    default:
      return String(b);
  }
}

function baseValue(b: Baseline, [block, key]: [keyof Baseline, string]): number | undefined {
  const rec = b[block] as unknown as Record<string, { value: number | null } | undefined>;
  const v = rec?.[key]?.value;
  return v === null ? undefined : v;
}

const num = (x: unknown): number | undefined => (typeof x === "number" && Number.isFinite(x) ? x : undefined);

/**
 * Normalise la sortie de `priceEffects` : rapport moteur `{ n1: { prices }, n5: { prices } }`, ou formes
 * `{ bread: { n1, n5 } }` / `{ bread: [n1, n5] }` (mock).
 */
export function normalizePriceEffects(raw: unknown): PriceEffects | null {
  if (!raw || typeof raw !== "object") return null;
  const out: PriceEffects = {};
  const r = raw as Record<string, unknown>;
  const p1 = (r.n1 as { prices?: Record<string, unknown> } | undefined)?.prices;
  const p5 = (r.n5 as { prices?: Record<string, unknown> } | undefined)?.prices;
  if (p1 && p5) {
    for (const k of ["bread", "fuel", "rent", "energy"] as PriceItem[]) {
      const n1 = num(p1[k]);
      const n5 = num(p5[k]);
      if (n1 !== undefined && n5 !== undefined) out[k] = { n1, n5 };
    }
    return Object.keys(out).length ? out : null;
  }
  for (const k of ["bread", "fuel", "rent", "energy"] as PriceItem[]) {
    const v = (raw as Record<string, unknown>)[k];
    if (Array.isArray(v)) {
      const n1 = num(v[0]);
      const n5 = num(v[v.length - 1]);
      if (n1 !== undefined && n5 !== undefined) out[k] = { n1, n5 };
    } else if (v && typeof v === "object") {
      const n1 = num((v as Record<string, unknown>).n1);
      const n5 = num((v as Record<string, unknown>).n5);
      if (n1 !== undefined && n5 !== undefined) out[k] = { n1, n5 };
    }
  }
  return Object.keys(out).length ? out : null;
}

export function normalizeAttribution(raw: unknown): AttributionItem[] | null {
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items) ? (raw as { items: unknown[] }).items : null;
  if (!arr) return null;
  const out: AttributionItem[] = [];
  for (const r of arr) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const kind = o.kind === "measure" || o.kind === "event" || o.kind === "financing" || o.kind === "shock" ? o.kind : undefined;
    if (!kind || typeof o.id !== "string") continue;
    const item: AttributionItem = { kind, id: o.id, label: typeof o.label === "string" ? o.label : o.id };
    if (typeof o.option_id === "string") item.option_id = o.option_id;
    if (typeof o.reference_option_id === "string") item.reference_option_id = o.reference_option_id;
    // Moteur : `delta` = réel − sans l'élément, clés KPI ; signes conservés (deficit_pct_gdp = solde signé).
    const delta = o.delta && typeof o.delta === "object" ? (o.delta as Record<string, unknown>) : o;
    const MAP = [
      ["d_deficit_pct", "deficit_pct_gdp"],
      ["d_debt_pct", "debt_pct_gdp"],
      ["d_unemployment_pt", "unemployment_pct"],
      ["d_purchasing_power", "purchasing_power_index"],
      ["d_growth_pt", "growth_avg_pct"],
    ] as const;
    for (const [key, eng] of MAP) {
      const v = num(delta[eng]) ?? num(delta[key]);
      if (v !== undefined) item[key] = v;
    }
    if (typeof o.rationale === "string") item.rationale = o.rationale;
    out.push(item);
  }
  return out;
}

const NO_CAPS = (): EngineCaps => ({ kpis: new Set(), priceEffects: false, attribution: false, variants: false, missionCuts: false, verdictRules: false });

/* ------------------------------------------------------------------ verdict expliqué */

/** `verdict.rules` du moteur, lu défensivement ; null si absent ou mal formé. */
export function normalizeRules(raw: unknown): VerdictRule[] | null {
  if (!Array.isArray(raw)) return null;
  const out: VerdictRule[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    let value = num(o.value);
    let threshold = num(o.threshold);
    if (typeof o.id !== "string" || typeof o.label !== "string" || value === undefined || threshold === undefined || typeof o.ok !== "boolean") continue;
    // Moteur v0.3 : `comparator` (min / min_strict) et `kind` = nature (economy…) ; solde signé (négatif = déficit). Affichage : le
    // déficit en positif avec une « limite », les autres règles avec un « minimum ».
    let kind: "max" | "min" | undefined = o.kind === "max" || o.kind === "min" ? o.kind : typeof o.comparator === "string" ? "min" : undefined;
    let unit = typeof o.unit === "string" ? o.unit : undefined;
    if (o.id === "deficit" && (unit === "pct_gdp" || unit === undefined) && typeof o.comparator === "string") {
      value = -value;
      threshold = -threshold;
      kind = "max";
      unit = "% du PIB";
    }
    const a = o.alt && typeof o.alt === "object" ? (o.alt as Record<string, unknown>) : null;
    const alt = a && typeof a.label === "string" && num(a.value) !== undefined && num(a.threshold) !== undefined && typeof a.ok === "boolean" ? { label: a.label, value: num(a.value)!, threshold: num(a.threshold)!, ok: a.ok } : undefined;
    out.push({
      id: o.id,
      label: o.label,
      value,
      threshold,
      ok: o.ok,
      ...(unit !== undefined ? { unit } : {}),
      ...(kind ? { kind } : {}),
      ...(typeof o.param === "string" ? { param: o.param } : {}),
      ...(alt ? { alt } : {}),
    });
  }
  return out.length ? out : null;
}

/** Seuil de majorité : param `parliament_majority_threshold`, sinon `majority_loss_threshold`, sinon moitié de l'échelle 0–100 (ADR 0006 §6). */
export function majorityThresholdOf(params: ParamSet): number {
  return params.parliament_majority_threshold?.value ?? params.majority_loss_threshold?.value ?? 100 / 2;
}

/**
 * Règles du verdict : celles du moteur si `verdict.rules` existe, sinon dérivées des paramètres que le verdict actuel lit
 * (engine/src/simulate.ts#verdict) et des KPIs de la dernière année. Le ✓/✗ vient de `blocked_by` quand il existe :
 * l'UI ne refait pas le jugement, elle l'explique.
 */
export function verdictRules(params: ParamSet, timeline: Timeline): VerdictRule[] {
  const fromEngine = normalizeRules((timeline.verdict as unknown as { rules?: unknown } | undefined)?.rules);
  if (fromEngine) return fromEngine;
  const last = timeline.years[timeline.years.length - 1];
  if (!last) return [];
  const k = last.kpis;
  const P = (id: string): number | undefined => num(params[id]?.value);
  const blocked = timeline.verdict?.blocked_by;
  const okOf = (b: "capital" | "cohesion" | "confidence" | "deficit", fallback: boolean) => (blocked ? !blocked.includes(b) : fallback);
  const out: VerdictRule[] = [];
  // Repli (moteur sans `verdict.rules`) : ancrage du traité (`deficit_rule_pct_gdp`), le moteur v0.3 publie toujours ses règles.
  const dmax = P("deficit_rule_pct_gdp");
  const bal = num(k.deficit_pct_gdp);
  if (dmax !== undefined && bal !== undefined) out.push({ id: "deficit", label: `Déficit public ${last.year}`, value: -bal, threshold: dmax, ok: okOf("deficit", -bal <= dmax), unit: "% du PIB", kind: "max", derived: true, param: "deficit_rule_pct_gdp" });
  const conf = num(k.confidence);
  const confLow = P("confidence_low");
  if (conf !== undefined && confLow !== undefined) out.push({ id: "confidence", label: "Confiance des marchés", value: conf, threshold: confLow, ok: okOf("confidence", conf >= confLow), kind: "min", derived: true });
  const cap = num(k.political_capital);
  const capMin = P("reelection_capital_min");
  const w = P("verdict_confidence_weight");
  if (cap !== undefined && capMin !== undefined) {
    const electoral = cap + (w !== undefined && conf !== undefined && confLow !== undefined ? w * (conf - confLow) : 0);
    out.push({ id: "capital", label: "Soutien électoral (capital politique, confiance des marchés comprise)", value: electoral, threshold: capMin, ok: okOf("capital", electoral > capMin), kind: "min", derived: true });
  }
  const coh = num(k.cohesion);
  const cohMin = P("reelection_cohesion_min");
  if (coh !== undefined && cohMin !== undefined) out.push({ id: "cohesion", label: "Cohésion sociale", value: coh, threshold: cohMin, ok: okOf("cohesion", coh > cohMin), kind: "min", derived: true });
  const maj = num(k.parliament_majority);
  if (maj !== undefined) {
    const t = majorityThresholdOf(params);
    out.push({ id: "majority", label: "Majorité au Parlement (pour gouverner : sinon censure possible)", value: maj, threshold: t, ok: maj > t, unit: "% des sièges", kind: "min", derived: true, governing: true });
  }
  return out;
}

/* ------------------------------------------------------------------ économies ciblées par mission */

export interface MissionCutGroup {
  id: string;
  label: string;
  /** Plafond en % (param `cut_<groupe>_max_pct`). */
  max: number;
  param: Param;
  /** Coût politique par point (param `cut_<groupe>_political_cost_per_pt`, sinon celui du rabot). */
  polPerPt?: number;
  /** Missions LOLF du groupe si les params les déclarent (`mission` du param), sinon déduites de la simulation. */
  missions?: string[];
}

/** Groupes de missions ouverts aux économies ciblées : un par paramètre `cut_<groupe>_max_pct` (contrat v0.3 supposé). */
export function missionCutGroups(params: ParamSet): MissionCutGroup[] {
  const out: MissionCutGroup[] = [];
  for (const [key, p] of Object.entries(params)) {
    const m = /^cut_([a-z0-9_]+)_max_pct$/.exec(key);
    if (!m || p.value === null || !(p.value > 0)) continue;
    const id = m[1]!;
    const pol = params[`cut_${id}_political_cost_per_pt`]?.value ?? params.cut_political_cost_per_pt?.value ?? undefined;
    const label = p.label.split(" : ")[0]!.replace(/^(Économies ciblées|Economies ciblées|Plafond d'économies)\s*(—|-|:)?\s*/i, "").trim() || id;
    out.push({ id, label, max: p.value, param: p, ...(pol !== undefined && pol !== null ? { polPerPt: pol } : {}), ...(p.mission ? { missions: p.mission.split(/[,\s]+/).filter(Boolean) } : {}) });
  }
  return out;
}

const realEngine: EngineApi = {
  name: "engine/src",
  isMock: false,
  caps: NO_CAPS(),
  simulate: (ctx, programme, financing, choices, seed, mode) =>
    engine.simulate(ctx.baseline, ctx.params, programme, financing, choices, seed, { measures: ctx.measures, events: ctx.events }, simOpts(mode)),
  trend: (ctx, seed, mode) =>
    engine.simulate(ctx.baseline, ctx.params, { measure_ids: [], hyp: {} }, { revenue_raises: {}, across_the_board_cut: 0 }, {}, seed, { measures: ctx.measures, events: [] }, simOpts(mode)),
  scorecard: (ctx, programme, financing, choices, seed, mode) => {
    const c = scorecardContent(ctx);
    return c ? engine.scorecard(ctx.baseline, ctx.params, programme, financing, choices, seed, c, { realistic: mode.realistic }) : null;
  },
  explainScorecard: (ctx, programme, financing, choices, seed, mode) => {
    const c = scorecardContent(ctx);
    return c ? engine.explainScorecard(ctx.baseline, ctx.params, programme, financing, choices, seed, c, { realistic: mode.realistic }) : null;
  },
  priceEffects: (ctx, programme, financing) => normalizePriceEffects(engine.priceEffects(ctx.params, programme, financing, ctx.measures)),
  attribute2032: (ctx, programme, financing, choices, seed) =>
    normalizeAttribution(engine.attribute2032(ctx.baseline, ctx.params, programme, financing, choices, seed, { measures: ctx.measures, events: ctx.events })),
  needGap: (ctx, programme) => engine.needGap(programme, ctx.measures),
  previewFinancing: (ctx, programme, financing, seed) => engine.previewProgramme(ctx.baseline, ctx.params, programme, financing, ctx.measures, seed),
  measureTrajectory: (ctx, id, hyp) => engine.measureTrajectory(ctx.baseline, ctx.params, ctx.measures, id, hyp[id]),
  projectRemaining: (ctx, programme, financing, choices, seed, from, mode) => {
    const { game, state } = engine.simulateWithState(ctx.baseline, ctx.params, programme, financing, choices, seed, { measures: ctx.measures, events: ctx.events }, from + 1, simOpts(mode));
    return engine.projectRemaining(game, state);
  },
  verdict: (ctx, timeline) => {
    const last = timeline.years[timeline.years.length - 1];
    if (!last || !timeline.verdict) {
      return { score: 0, score_max: 0, reelected: false, headline: "Mandat interrompu.", lines: [], flags: timeline.game_over ? [timeline.game_over.reason] : [], rules: [] };
    }
    const v = timeline.verdict;
    const lines: Verdict["lines"] = [];
    for (const l of VERDICT_LINES) {
      const from = baseValue(ctx.baseline, l.base);
      const to = last.kpis[l.key];
      if (from !== undefined && to !== undefined) lines.push({ label: l.label, from, to, unit: l.unit, dec: l.dec });
    }
    return {
      score: v.score,
      score_max: v.score_max,
      reelected: v.reelected,
      headline: v.reelected ? "Réélu." : "Battu.",
      lines,
      flags: v.blocked_by.map((b) => blockedLabel(ctx.params, b)),
      rules: verdictRules(ctx.params, timeline),
    };
  },
};

/**
 * Sonde des capacités v0.2 : KPIs présents dans une Timeline, fonctions exportées qui répondent sans lever,
 * et prise en compte des variantes (le besoin de financement change-t-il quand on choisit une variante ?).
 */
export function probeCaps(api: EngineApi, ctx: EngineContext): EngineCaps {
  const caps = NO_CAPS();
  const empty: Programme = { measure_ids: [], hyp: {} };
  const noFin: Financing = { revenue_raises: {}, across_the_board_cut: 0 };
  try {
    const tl = api.simulate(ctx, empty, noFin, {}, 1);
    const first = tl.years[0];
    if (first) for (const k of V02_KPIS) if (num(first.kpis[k]) !== undefined) caps.kpis.add(k);
  } catch {
    /* sonde : pas de KPI v0.2 */
  }
  try {
    const tl = api.simulate(ctx, empty, noFin, {}, 1);
    caps.verdictRules = normalizeRules((tl.verdict as unknown as { rules?: unknown } | undefined)?.rules) !== null;
  } catch {
    caps.verdictRules = false;
  }
  // Économies par mission : la simulation (sans crise) change-t-elle quand on en fournit au plafond ?
  const groups = missionCutGroups(ctx.params);
  if (groups.length) {
    try {
      const quiet = { ...ctx, events: [] };
      const a = api.simulate(quiet, empty, noFin, {}, 1);
      const fin: UiFinancing = { ...noFin, mission_cuts: Object.fromEntries(groups.map((g) => [g.id, g.max])) };
      const b = api.simulate(quiet, empty, fin, {}, 1);
      caps.missionCuts = JSON.stringify(a.years.map((y) => y.kpis)) !== JSON.stringify(b.years.map((y) => y.kpis));
    } catch {
      caps.missionCuts = false;
    }
  }
  try {
    caps.priceEffects = api.priceEffects(ctx, empty, noFin) !== null;
  } catch {
    caps.priceEffects = false;
  }
  try {
    caps.attribution = api.attribute2032(ctx, empty, noFin, {}, 1) !== null;
  } catch {
    caps.attribution = false;
  }
  // Variantes : le besoin de financement change-t-il quand on choisit une variante dont le coût diffère du catalogue ?
  const vm = ctx.measures.find((m) => m.variants?.some((v) => Math.abs(lastOfProfile(v.cost) - lastOfProfile(m.cost)) > 1e-6));
  if (vm) {
    const v = vm.variants!.find((x) => Math.abs(lastOfProfile(x.cost) - lastOfProfile(vm.cost)) > 1e-6)!;
    try {
      const base = api.needGap(ctx, { measure_ids: [vm.id], hyp: {} });
      const native = api.needGap(ctx, { measure_ids: [vm.id], hyp: {}, variants: { [vm.id]: v.id } });
      caps.variants = Math.abs(native - base) > 1e-6;
    } catch {
      caps.variants = false;
    }
  }
  return caps;
}

const lastOfProfile = (v: number | number[]) => (Array.isArray(v) ? (v[v.length - 1] ?? 0) : v);

/** Coût en régime (Md€/an) d'une variante du catalogue ; undefined si la variante n'existe pas. */
export function variantCost(m: UiMeasure, variantId: string): number | undefined {
  const v = m.variants?.find((x) => x.id === variantId);
  return v ? lastOfProfile(v.cost) : undefined;
}

/** Programme transmis au moteur : variantes des mesures cochées (lues nativement) ; retirées si le moteur ne les lit pas. */
export function engineProgramme(api: EngineApi, p: UiProgramme): Programme {
  const variants = p.variants ?? {};
  const ids = Object.keys(variants).filter((id) => p.measure_ids.includes(id));
  if (!ids.length || !api.caps.variants) return { measure_ids: p.measure_ids, hyp: p.hyp };
  return { measure_ids: p.measure_ids, hyp: p.hyp, variants: Object.fromEntries(ids.map((id) => [id, variants[id]!])) };
}

let cached: EngineApi | null = null;
let lastError: string | null = null;

/** Pourquoi le mock est actif (null si moteur réel). */
export function engineFallbackReason(): string | null {
  return lastError;
}

/** Moteur réel par défaut ; mock seulement sur `?mock=1` ou si le moteur réel refuse le contenu (erreur mémorisée). */
export function getEngine(ctx: EngineContext, forceMock = false): EngineApi {
  if (cached) return cached;
  let realOk = false;
  try {
    realEngine.simulate(ctx, { measure_ids: [], hyp: {} }, { revenue_raises: {}, across_the_board_cut: 0 }, {}, 1);
    realOk = true;
  } catch (err) {
    lastError = String(err instanceof Error ? err.message : err);
  }
  if (forceMock) {
    lastError = "mock demandé (?mock=1)";
    cached = mockEngine;
  } else cached = realOk ? realEngine : mockEngine;
  cached.caps = probeCaps(cached, ctx);
  return cached;
}
